/**
 * React Query hooks for the public Raises directory + profiles (sub-project ③B).
 *
 * Mirrors `useActiveRaises.ts` (the admin equivalent):
 *  - the directory list keeps previous data only across pages of the same
 *    viewer-scoped result set (10m fresh / 30m gc); its contextual facets ride
 *    on page 0 of that same request (`includeFacets`), never a separate query,
 *  - profile / company detail are gated by an `enabled` slug guard and skip
 *    retrying a 404 (a genuinely-missing slug should fail fast to the not-found
 *    page rather than retry three times).
 *
 * Query keys are stable + serializable: the directory query object is included
 * verbatim so distinct filter/sort/page combinations cache independently, and
 * the slug keys namespace profile vs company.
 *
 * These hooks are anonymous-friendly end-to-end: the client sends a token only
 * when one exists and the server resolves the tier — the hooks neither know nor
 * compute the viewer tier (it arrives on the response envelope).
 */

import { useQuery } from '@tanstack/react-query';
import raisesApi, { RaisesNotFoundError } from '@/api/raisesApi';
import { useAuth } from '@/hooks/useAuth';
import type { InvestorCapitalStackDto } from '@/components/raises/capitalStack/types';
import type { DataroomViewAs } from '@/types/ownerFiles';
import type { DirectoryQuery, RaiseViewAs } from '@/types/raises';
import { directoryResultSetSignature } from '@/components/raises/helpers/directoryQuery';

const ANONYMOUS_VIEWER = 'anonymous';

export const raisesKeys = {
  all: ['raises'] as const,
  aggregate: ['raises', 'aggregate'] as const,
  directory: (query: DirectoryQuery) => ['raises', 'directory', query] as const,
  directoryForViewer: (query: DirectoryQuery, viewerScope: string) =>
    [...raisesKeys.directory(query), viewerScope] as const,
  profile: (slug: string | null) => ['raises', 'profile', slug] as const,
  profileForViewer: (slug: string | null, viewerScope: string) =>
    [...raisesKeys.profile(slug), viewerScope] as const,
  company: (slug: string | null) => ['raises', 'company', slug] as const,
  companyForViewer: (slug: string | null, viewerScope: string) =>
    [...raisesKeys.company(slug), viewerScope] as const,
  capitalStack: (slug: string | null) => ['raises', 'capital-stack', slug] as const,
  capitalStackForViewer: (slug: string | null, viewerScope: string) =>
    [...raisesKeys.capitalStack(slug), viewerScope] as const,
};

const MINUTE = 60 * 1000;

/** Privacy-banded directory capital total used by the anonymous landing hero. */
export function useDirectoryCapitalAggregate() {
  return useQuery({
    queryKey: raisesKeys.aggregate,
    queryFn: () => raisesApi.getDirectoryCapitalAggregate(),
    staleTime: 10 * MINUTE,
    gcTime: 30 * MINUTE,
  });
}

/**
 * `retry` predicate that never retries a not-found: a missing slug is a terminal
 * 404 (render the not-found page), not a transient error worth retrying. Other
 * errors fall back to React Query's default bounded retry (up to 3).
 */
function retryUnlessNotFound(failureCount: number, error: unknown): boolean {
  if (error instanceof RaisesNotFoundError) return false;
  return failureCount < 3;
}

/**
 * The cache-partitioning scope for a viewer. Base is the signed-in uid (or the
 * anonymous sentinel). An ADMIN-ONLY `viewAs` override is folded in as a suffix so
 * each simulated perspective caches INDEPENDENTLY of the admin's real view (and of
 * each other) — a `viewAs=anonymous` render can never be served from, or bleed
 * into, the admin's own entitled cache. Absent `viewAs` yields the exact base
 * scope, so every non-view-as caller is byte-for-byte unchanged.
 */
function viewerScope(
  user: { uid?: string } | null | undefined,
  viewAs?: RaiseViewAs | DataroomViewAs,
): string {
  const base = user?.uid ? `user:${user.uid}` : ANONYMOUS_VIEWER;
  return viewAs ? `${base}|viewAs:${viewAs}` : base;
}

/**
 * The tier-aware directory list for a given query. Keeps the previous page on
 * screen while the next page of that same viewer/filter/sort result set loads.
 */
export function useRaisesDirectory(query: DirectoryQuery) {
  const { user, isLoading: authLoading } = useAuth();
  const scope = viewerScope(user);

  const result = useQuery({
    queryKey: raisesKeys.directoryForViewer(query, scope),
    queryFn: () => raisesApi.listProjects(query),
    enabled: !authLoading,
    staleTime: 10 * MINUTE,
    gcTime: 30 * MINUTE,
    // React Query passes the observer's previous query here when the key changes.
    // Preserve the smooth same-viewer transition between pages of the SAME result
    // set, but never project one identity's response into another identity or a
    // different filter/sort key while its request is pending. The final key segment
    // is the opaque viewer scope used by `directoryForViewer`; no tier or
    // classification inference happens client-side.
    placeholderData: (previousData, previousQuery) => {
      const previousKey = previousQuery?.queryKey;
      const previousScope = previousKey?.[previousKey.length - 1];
      const previousInput = previousKey?.[2];
      const sameResultSet =
        typeof previousInput === 'object' &&
        previousInput !== null &&
        directoryResultSetSignature(previousInput as DirectoryQuery) ===
          directoryResultSetSignature(query);
      return previousScope === scope && sameResultSet ? previousData : undefined;
    },
  });

  // The directory page uses this exact opaque cache identity to scope its own
  // cross-page accumulator. Returning it from the hook prevents a second, subtly
  // divergent auth-scope implementation at the render layer.
  return { ...result, viewerScope: scope };
}

/**
 * The tier-scoped project profile for a slug. Disabled until a slug is present;
 * a 404 fails fast (no retry) so the page can render its not-found state.
 *
 * `viewAs` is the ADMIN-ONLY "view-as perspective" DTO-tier override: selecting a
 * perspective re-fetches the SAME slug under a distinct cache scope so the page
 * re-renders faithfully as that audience (redaction, gating, connect-availability
 * all reflect the server's recomputed tier). Omit it (the default) for the admin's
 * own real view — the query is then identical to every non-admin caller's. The
 * server ignores `viewAs` for a non-admin, so it is only ever sent by the
 * admin-gated control.
 */
export function useRaiseProfile(slug: string | null, viewAs?: RaiseViewAs) {
  const { user, isLoading: authLoading } = useAuth();
  const scope = viewerScope(user, viewAs);

  return useQuery({
    queryKey: raisesKeys.profileForViewer(slug, scope),
    queryFn: () =>
      viewAs ? raisesApi.getProject(slug as string, viewAs) : raisesApi.getProject(slug as string),
    enabled: !authLoading && slug !== null && slug.length > 0,
    staleTime: 10 * MINUTE,
    gcTime: 30 * MINUTE,
    retry: retryUnlessNotFound,
  });
}

/**
 * The PUBLIC, tier-gated capital-stack run for a raise — the read the entitled
 * investor view (and the admin "view-as owner" preview) consume, via the
 * generation-free `GET /api/raises/:slug/capital-stack`. The server authorizes the
 * read (entitled / verified owner / admin) and 404s everyone else; the api client maps
 * that 404 (and "no visible run") to `null`, so this query resolves to `null` whenever
 * there is nothing to render and the caller shows nothing.
 *
 * `enabled` lets the mount site opt in only when the module could plausibly render (a
 * present slug + an entitled/owner-preview viewer) — the query is never fired
 * speculatively for a free/anonymous viewer. Cache is partitioned by viewer scope so an
 * entitled render never bleeds across identities. This is DISTINCT from `useCapitalStack`
 * (in `useOwner.ts`), which reads the OWNER-gated route for the verified owner's own
 * generate-capable panel.
 *
 * `viewAs` is the ADMIN-ONLY canonical-admin PREVIEW override (round-9 #7). The admin
 * "view-as owner" preview passes `viewAs='owner'` so the request URL carries `?viewAs=owner`
 * AND the cache key partitions independently of a real entitled read (an admin preview and
 * a genuine entitled investor never share a cache entry, and the preview never bleeds into
 * the admin's own real view). The server re-verifies the canonical Firestore admin role
 * (fail-closed; the token role is never trusted) before honouring it. Omitted (the default)
 * for a real entitled viewer — the request is then the bare URL under the base scope.
 */
export function usePublicCapitalStack(
  slug: string | null | undefined,
  options: { enabled?: boolean; viewAs?: DataroomViewAs } = {},
) {
  const { user, isLoading: authLoading } = useAuth();
  const { viewAs } = options;
  const scope = viewerScope(user, viewAs);
  const optIn = options.enabled ?? true;
  const hasSlug = typeof slug === 'string' && slug.length > 0;

  return useQuery<InvestorCapitalStackDto | null>({
    queryKey: raisesKeys.capitalStackForViewer(hasSlug ? slug : null, scope),
    queryFn: () => raisesApi.getPublicCapitalStack(slug as string, viewAs),
    enabled: !authLoading && optIn && hasSlug,
    staleTime: 2 * MINUTE,
    gcTime: 10 * MINUTE,
  });
}

/**
 * The company profile (facts + teaser project rollup) for a slug. Disabled until
 * a slug is present; a 404 fails fast (no retry) for the not-found state.
 */
export function useCompanyProfile(slug: string | null) {
  const { user, isLoading: authLoading } = useAuth();
  const scope = viewerScope(user);

  return useQuery({
    queryKey: raisesKeys.companyForViewer(slug, scope),
    queryFn: () => raisesApi.getCompany(slug as string),
    enabled: !authLoading && slug !== null && slug.length > 0,
    staleTime: 10 * MINUTE,
    gcTime: 30 * MINUTE,
    retry: retryUnlessNotFound,
  });
}
